import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  inject,
  Input,
  OnChanges,
  output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { distinctUntilChanged, map, startWith } from 'rxjs';

import { SEARCH_AND_FILTERS_ELEMENT_SLOT_ID } from '@blaze-runtime/engine-domain';
import {
  type ElementHorizontalAlignment,
  getDirectionValueMapFromCssProperty,
  type LabelIconAlignment,
  makeFontStyleStyles,
  makeLabelHorizontalAlignStyles,
  type PropertiesConfigInputFontStyleOptions,
  type PropertiesConfigSearchAndFiltersModel,
  ViewportMode,
} from '@blaze-shared/elements-domain';
import { ButtonIconComponent, ClickableDirective, TooltipDirective } from '@blaze-shared/shared-ui';
import { mutationRxUtils } from '@blaze-shared/shared-util';
import { FontsService, IconsModule, ThemesService } from '@blaze-shared/theming-data-access';
import { FALLBACK_THEME } from '@blaze-shared/theming-domain';

import { type ComponentSlotData } from '../../canvas-elements/element-component';
import { SlotNodeDirective } from '../../canvas-elements/rendering/slot-node.directive';
import { ViewportService } from '../../services/viewport.service';

@UntilDestroy()
@Component({
  selector: 'blaze-element-filter-header',
  templateUrl: './element-filter-header.component.html',
  styleUrls: [
    './element-filter-header.component.scss',
    './element-filter-header.component.desktop.scss',
    './element-filter-header.component.mobile.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    IconsModule,
    TooltipDirective,
    SlotNodeDirective,
    ButtonIconComponent,
    ClickableDirective,
  ],
})
export class ElementFilterHeaderComponent implements OnChanges, AfterViewInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fontsService = inject(FontsService);
  private readonly themesService = inject(ThemesService);
  private readonly viewportService = inject(ViewportService);

  addButtonClick = output<MouseEvent>();

  @Input() fontColor? = FALLBACK_THEME.value.title;

  @Input() labelEnabled? = true;
  @Input() labelText? = '';
  @Input() labelFontFamily? = 'var(--bat-headerFontFamily)';
  @Input() labelFontSize? = 'var(--bat-headerFontSize)';
  @Input() labelFontWeight? = 'var(--bat-headerFontWeight)';
  @Input() labelFontStyle?: PropertiesConfigInputFontStyleOptions;
  @Input() labelTextAlign?: ElementHorizontalAlignment = 'left';
  @Input() labelIcon? = '';
  @Input() labelIconAlign?: LabelIconAlignment = 'left';
  @Input() labelIconSize?: string = 'var(--bat-headerIconSize)';
  @Input() labelIconColor?: string = '';

  @Input() searchAndFilters?: PropertiesConfigSearchAndFiltersModel;
  @Input() slots?: ComponentSlotData;
  @Input() filterBuilderTriggerTpl: TemplateRef<{ fullWidth: boolean }> | null = null;
  @Input() addButtonEnabled? = false;
  @Input() addButtonLabel? = '';
  @Input() addButtonBlocked? = false;
  @Input() addButtonCssClasses?: string[] = [];

  @HostBinding('class.pb-16') @Input() hasBottomSpacing = true;

  @ViewChild('buttons') buttonsRef?: ElementRef<HTMLDivElement>;
  hasButtons = false;
  viewportMode: ViewportMode | undefined;
  labelStyles: Record<string, string | undefined> = {};
  iconStyles: Record<string, string | undefined> = {};

  @HostBinding('class.!hidden') get isHidden(): boolean {
    if (this.injectSearch) {
      return !(
        this.labelEnabled ||
        this.hasSearch ||
        this.hasFilters ||
        this.hasButtons ||
        this.hasFilterBuilder
      );
    }
    return !(this.labelEnabled || this.hasButtons || this.hasFilterBuilder);
  }

  get injectSearch(): boolean {
    return this.searchAndFilters?.injectToElement ?? false;
  }

  get hasFilterBuilder(): boolean {
    return this.searchAndFilters?.mode === 'FILTER_BUILDER';
  }

  get hasSearch(): boolean {
    return this.searchAndFilters?.searchEnabled ?? false;
  }

  get hasApiParams(): boolean {
    return (
      (this.slots?.slotIds.includes(SEARCH_AND_FILTERS_ELEMENT_SLOT_ID) &&
        !this.searchAndFilters?.searchEnabled) ??
      false
    );
  }

  get hasFilters(): boolean {
    return (
      (!!this.searchAndFilters?.filters?.length || this.hasApiParams) && !this.hasFilterBuilder
    );
  }

  get shouldForceExpand(): boolean {
    return (this.injectSearch && this.viewportMode === 'mobile') ?? false;
  }

  get slotPaddingCompensation(): string | undefined {
    const spacing = this.themesService.activeTheme?.value.spacingSearchAndFilters;
    if (!spacing) {
      return undefined;
    }
    const { top, right, bottom, left } = getDirectionValueMapFromCssProperty(spacing);
    return `-${top} -${right} -${bottom} -${left}`;
  }

  ngOnChanges(): void {
    this.updateStyles();
  }

  ngAfterViewInit(): void {
    // Use mutation observer to detect if buttons are present in projected content
    mutationRxUtils
      .fromMutationObserver(this.buttonsRef?.nativeElement, { childList: true })
      .pipe(
        startWith([]),
        map(() => !!this.buttonsRef?.nativeElement.childElementCount),
        distinctUntilChanged(),
        untilDestroyed(this),
      )
      .subscribe((hasButtons) => {
        this.hasButtons = hasButtons;
        this.cdr.markForCheck();
      });

    this.viewportService.mode$.pipe(untilDestroyed(this)).subscribe((mode) => {
      this.viewportMode = mode;
      this.cdr.detectChanges();
    });
  }

  private updateStyles(): void {
    const labelStyles: Record<string, string> = {
      ...makeLabelHorizontalAlignStyles(this.labelTextAlign),
      ...makeFontStyleStyles(this.labelFontStyle),
      fontFamily: this.labelFontFamily ?? 'var(--bat-headerFontFamily)',
      fontSize: this.labelFontSize ?? 'var(--bat-headerFontSize)',
      fontWeight: this.fontsService.getFontWeight(this.labelFontFamily, this.labelFontWeight),
    };

    if (this.labelIconAlign === 'right') {
      labelStyles['flex-direction'] = 'row-reverse';
    }

    const iconStyles: Record<string, string | undefined> = {
      '--icon-size': this.labelIconSize ?? 'var(--bat-headerIconSize)',
      marginLeft: this.labelIconAlign === 'right' ? '-0.3em' : undefined,
      color: this.labelIconColor,
    };

    this.labelStyles = labelStyles;
    this.iconStyles = iconStyles;
  }
}
